/**
 * MotionBufferService - Bidirectional motion buffer loading and processing
 *
 * Handles:
 * - Bidirectional loading (past + future) via parallel requests
 * - Buffer merging with duplicate removal
 * - Client-side caching
 * - Planet position parsing and interpolation
 */

import { devLog, devWarn, devError } from '../utils/devLog';
import { jatakaApiService } from './JatakaApiService';
import { MOTION_TIMING } from '../config/timingConfig';
import type {
  PlanetPosition,
  AllPlanetsSnapshot,
  DegreeMotionBuffer,
  DegreeMotionPoint,
  TimeMotionBuffer,
  TimeMotionPoint,
  TimeStepUnit,
} from '../types/MotionTypes';

// =============================================================================
// Progressive Loading Configuration
// =============================================================================

const POLL_INTERVAL_MS = MOTION_TIMING.POLL_INTERVAL;
const MAX_POLL_ATTEMPTS = MOTION_TIMING.MAX_POLL_ATTEMPTS;

// =============================================================================
// Cache Configuration
// =============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 20;

interface CacheEntry<T> {
  buffer: T;
  timestamp: number;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format cache key base: time to minute, coords to 4 decimals
 * Synced with backend cache key format
 */
function formatCacheKeyBase(time: Date, lat: number, lon: number): string {
  const timeKey = Math.floor(time.getTime() / (1000 * 60));
  return `${timeKey}_${lat.toFixed(4)}_${lon.toFixed(4)}`;
}

/**
 * Format Date with timezone offset for API
 * Date is expected to store local time values using UTC methods (Date.UTC)
 * Just formats the stored values and appends the timezone offset suffix
 */
function formatDateWithTimezone(date: Date, timezoneOffset: number): string {
  const offsetSign = timezoneOffset >= 0 ? '+' : '-';
  const offsetHours = Math.floor(Math.abs(timezoneOffset));
  const offsetMinutes = Math.round((Math.abs(timezoneOffset) - offsetHours) * 60);
  const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  // Date stores local time values using UTC (to avoid browser timezone interference)
  // Just extract and format - don't add offset again
  // Note: Include milliseconds for maximum precision (backend preserves microseconds)
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  const ms = String(date.getUTCMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${offsetStr}`;
}

// =============================================================================
// MotionBufferService Class
// =============================================================================

class MotionBufferService {
  private degreeCache: Map<string, CacheEntry<DegreeMotionBuffer>> = new Map();
  private timeCache: Map<string, CacheEntry<TimeMotionBuffer>> = new Map();

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Load degree-based motion buffer with progressive loading support
   *
   * New API that returns exact degree steps instead of time intervals.
   * Supports progressive loading: initial ±10 points returned immediately,
   * remaining points loaded via polling.
   *
   * @param planet - Planet code (Lg, Sy, Ch, etc.)
   * @param startTime - Start time for the buffer
   * @param lat - Observer latitude
   * @param lon - Observer longitude
   * @param timezoneOffset - UTC offset in hours
   * @param degreeRange - Range in degrees (360 = ±360°)
   * @param degreeStep - Step size (1.0 or 0.5)
   * @param onProgress - Progress callback with real progress (0-100)
   * @param onInitialData - Called when initial data is available (for immediate UI)
   * @param abortSignal - Optional AbortSignal for cancellation
   */
  async loadDegreeBuffer(
    planet: string,
    startTime: Date,
    lat: number,
    lon: number,
    timezoneOffset: number,
    degreeRange: number = 360,
    degreeStep: number = 1.0,
    onProgress?: (progress: number) => void,
    onInitialData?: (buffer: DegreeMotionBuffer) => void,
    abortSignal?: AbortSignal,
    onBufferUpdate?: (buffer: DegreeMotionBuffer) => void,
    forceRefresh: boolean = false
  ): Promise<DegreeMotionBuffer | null> {
    // Check frontend cache first (skip if forceRefresh)
    const cacheKey = this.createDegreeCacheKey(planet, startTime, lat, lon, degreeRange, degreeStep);
    if (!forceRefresh) {
      const cached = this.getFromDegreeCache(cacheKey);
      if (cached && cached.isComplete) {
        devLog(`[MotionBuffer] Cache hit (complete buffer) for step ${degreeStep}°`);
        onProgress?.(100);
        return cached;
      }
    } else {
      devLog(`[MotionBuffer] Force refresh requested, skipping cache`);
    }

    // Format start time with timezone offset for API
    const startTimeISO = formatDateWithTimezone(startTime, timezoneOffset);

    // Initial request
    const result = await jatakaApiService.getDegreeMotionBuffer({
      planet,
      startTime: startTimeISO,
      latitude: lat,
      longitude: lon,
      timezoneOffset,
      degreeRange,
      degreeStep,
      forceRefresh,
    });

    if (!result.success || !result.data) {
      devError('[MotionBuffer] Failed to load degree motion buffer:', result.error);
      return null;
    }

    const response = result.data;

    // Use backend's centerIndex - it knows the correct center for the full buffer
    // During progressive loading, initial data may have d=0 at wrong position
    const centerIndex = response.centerIndex;
    devLog(`[MotionBuffer] Using backend centerIndex: ${centerIndex}`)

    // Create initial buffer
    let buffer: DegreeMotionBuffer = {
      planet: response.planet,
      centerIndex,
      data: [...response.data],
      startTime,
      isComplete: response.status === 'complete',
      taskId: response.status === 'calculating' ? response.taskId : undefined,
      progress: response.progress,
    };

    // Report initial progress
    onProgress?.(response.progress);

    // If complete, cache and return
    if (response.status === 'complete') {
      devLog(`[MotionBuffer] Complete response (cached: ${response.cached}), ${buffer.data.length} points`);
      this.addToDegreeCache(cacheKey, buffer);
      return buffer;
    }

    // Progressive loading: notify caller of initial data
    devLog(`[MotionBuffer] Progressive loading started: ${buffer.data.length}/${response.totalPoints} points`);
    onInitialData?.(buffer);

    // Start polling for remaining data
    const taskId = response.taskId;
    let lastIndex = buffer.data.length;
    let pollCount = 0;

    while (pollCount < MAX_POLL_ATTEMPTS) {
      // Check for abort
      if (abortSignal?.aborted) {
        devLog('[MotionBuffer] Progressive loading aborted');
        return buffer; // Return partial data
      }

      await this.sleep(POLL_INTERVAL_MS);
      pollCount++;

      const statusResult = await jatakaApiService.getDegreeMotionStatus(taskId, lastIndex);

      if (!statusResult.success || !statusResult.data) {
        devWarn('[MotionBuffer] Status poll failed, continuing...');
        continue;
      }

      const status = statusResult.data;

      if (status.status === 'complete') {
        // Got complete data
        const finalData = status.data || buffer.data;
        // Recalculate centerIndex for complete data
        const newCenterIndex = finalData.findIndex(p => p.d === 0);
        buffer = {
          ...buffer,
          data: finalData,
          centerIndex: newCenterIndex >= 0 ? newCenterIndex : buffer.centerIndex,
          isComplete: true,
          progress: 100,
        };
        onProgress?.(100);
        onBufferUpdate?.(buffer); // Notify caller of complete buffer
        devLog(`[MotionBuffer] Progressive loading complete: ${buffer.data.length} points, centerIndex: ${buffer.centerIndex}`);
        break;
      }

      // Merge new points
      if (status.newPoints && status.newPoints.length > 0) {
        const mergedData = this.mergePoints(buffer.data, status.newPoints);
        // IMPORTANT: Recalculate centerIndex after merge!
        // Data is sorted by degree offset, so find the index of d=0
        const newCenterIndex = mergedData.findIndex(p => p.d === 0);
        buffer = {
          ...buffer,
          data: mergedData,
          centerIndex: newCenterIndex >= 0 ? newCenterIndex : buffer.centerIndex,
          progress: status.progress,
        };
        lastIndex = status.lastIndex || lastIndex;
        onProgress?.(status.progress);
        onBufferUpdate?.(buffer); // Notify caller of updated buffer
        devLog(`[MotionBuffer] Merged ${status.newPoints.length} points, centerIndex: ${buffer.centerIndex}, progress: ${status.progress}%`);
      }
    }

    if (pollCount >= MAX_POLL_ATTEMPTS) {
      devWarn('[MotionBuffer] Max poll attempts reached, returning partial data');
    }

    // Cache final result
    this.addToDegreeCache(cacheKey, buffer);

    return buffer;
  }

  // ===========================================================================
  // Time Motion Buffer API
  // ===========================================================================

  /**
   * Load time-based motion buffer for chart time shifting
   *
   * Returns all planet positions at fixed time intervals.
   * Simpler than degree motion - no iterative search needed.
   *
   * @param startTime - Center time for the buffer
   * @param lat - Observer latitude
   * @param lon - Observer longitude
   * @param timezoneOffset - UTC offset in hours
   * @param stepUnit - Time unit: 'second', 'minute', 'hour'
   * @param stepValue - Step multiplier (1, 5, 15, 30, etc.)
   * @param range - Number of steps in each direction (default: 100)
   * @param onProgress - Progress callback (0-100)
   * @param onInitialData - Called when initial data is available
   * @param abortSignal - Optional AbortSignal for cancellation
   * @param onBufferUpdate - Called when buffer updates during progressive loading
   * @param forceRefresh - Skip cache and force recalculation
   */
  async loadTimeBuffer(
    startTime: Date,
    lat: number,
    lon: number,
    timezoneOffset: number,
    stepUnit: TimeStepUnit,
    stepValue: number,
    range: number = 100,
    onProgress?: (progress: number) => void,
    onInitialData?: (buffer: TimeMotionBuffer) => void,
    abortSignal?: AbortSignal,
    onBufferUpdate?: (buffer: TimeMotionBuffer) => void,
    forceRefresh: boolean = false
  ): Promise<TimeMotionBuffer | null> {
    // Check frontend cache first (skip if forceRefresh)
    const cacheKey = this.createTimeCacheKey(startTime, lat, lon, stepUnit, stepValue, range);
    if (!forceRefresh) {
      const cached = this.getFromTimeCache(cacheKey);
      if (cached && cached.isComplete) {
        devLog(`[MotionBuffer] Time cache hit (complete buffer) for ${stepValue} ${stepUnit}`);
        onProgress?.(100);
        return cached;
      }
    } else {
      devLog(`[MotionBuffer] Time force refresh requested, skipping cache`);
    }

    // Format start time with timezone offset for API
    const startTimeISO = formatDateWithTimezone(startTime, timezoneOffset);

    // Initial request
    const result = await jatakaApiService.getTimeMotionBuffer({
      startTime: startTimeISO,
      latitude: lat,
      longitude: lon,
      timezoneOffset,
      stepUnit,
      stepValue,
      range,
      forceRefresh,
    });

    if (!result.success || !result.data) {
      devError('[MotionBuffer] Failed to load time motion buffer:', result.error);
      return null;
    }

    const response = result.data;

    // Create initial buffer
    // centerIndex is always range value (e.g., 100 for range=100)
    let buffer: TimeMotionBuffer = {
      centerIndex: response.centerIndex,
      data: [...response.data],
      startTime,
      stepUnit: response.stepUnit,
      stepValue: response.stepValue,
      isComplete: response.status === 'complete',
      taskId: response.status === 'calculating' ? response.taskId : undefined,
      progress: response.progress,
    };

    // Report initial progress
    onProgress?.(response.progress);

    // If complete, cache and return
    if (response.status === 'complete') {
      devLog(`[MotionBuffer] Time buffer complete (cached: ${response.cached}), ${buffer.data.length} points`);
      this.addToTimeCache(cacheKey, buffer);
      return buffer;
    }

    // Progressive loading: notify caller of initial data
    devLog(`[MotionBuffer] Time progressive loading started: ${buffer.data.length}/${response.totalPoints} points`);
    onInitialData?.(buffer);

    // Start polling for remaining data
    const taskId = response.taskId;
    let lastIndex = buffer.data.length;
    let pollCount = 0;

    while (pollCount < MAX_POLL_ATTEMPTS) {
      // Check for abort
      if (abortSignal?.aborted) {
        devLog('[MotionBuffer] Time progressive loading aborted');
        return buffer; // Return partial data
      }

      await this.sleep(POLL_INTERVAL_MS);
      pollCount++;

      const statusResult = await jatakaApiService.getTimeMotionStatus(taskId, lastIndex);

      if (!statusResult.success || !statusResult.data) {
        devWarn('[MotionBuffer] Time status poll failed, continuing...');
        continue;
      }

      const status = statusResult.data;

      if (status.status === 'complete') {
        // Got complete data
        const finalData = status.data || buffer.data;
        // IMPORTANT: Recalculate centerIndex for complete data!
        // Find the point with i=0 (center offset)
        const newCenterIndex = finalData.findIndex(p => p.i === 0);
        buffer = {
          ...buffer,
          data: finalData,
          centerIndex: newCenterIndex >= 0 ? newCenterIndex : buffer.centerIndex,
          isComplete: true,
          progress: 100,
        };
        onProgress?.(100);
        onBufferUpdate?.(buffer);
        devLog(`[MotionBuffer] Time progressive loading complete: ${buffer.data.length} points, centerIndex: ${buffer.centerIndex}`);
        break;
      }

      // Merge new points
      if (status.newPoints && status.newPoints.length > 0) {
        const mergedData = this.mergeTimePoints(buffer.data, status.newPoints);
        // IMPORTANT: Recalculate centerIndex after merge!
        // Data is sorted by index offset, so find the index of i=0
        const newCenterIndex = mergedData.findIndex(p => p.i === 0);
        buffer = {
          ...buffer,
          data: mergedData,
          centerIndex: newCenterIndex >= 0 ? newCenterIndex : buffer.centerIndex,
          progress: status.progress,
        };
        lastIndex = status.lastIndex || lastIndex;
        onProgress?.(status.progress);
        onBufferUpdate?.(buffer);
        devLog(`[MotionBuffer] Time merged ${status.newPoints.length} points, centerIndex: ${buffer.centerIndex}, progress: ${status.progress}%`);
      }
    }

    if (pollCount >= MAX_POLL_ATTEMPTS) {
      devWarn('[MotionBuffer] Time max poll attempts reached, returning partial data');
    }

    // Cache final result
    this.addToTimeCache(cacheKey, buffer);

    return buffer;
  }

  /**
   * Merge new time points into existing buffer, maintaining sort order by index
   */
  private mergeTimePoints(existing: TimeMotionPoint[], newPoints: TimeMotionPoint[]): TimeMotionPoint[] {
    const merged = [...existing];
    const existingIndices = new Set(existing.map(p => p.i));

    for (const point of newPoints) {
      if (!existingIndices.has(point.i)) {
        merged.push(point);
        existingIndices.add(point.i);
      }
    }

    // Sort by index
    return merged.sort((a, b) => a.i - b.i);
  }

  /**
   * Get all planet positions at a specific index in time buffer
   */
  getAllPlanetsAtTimeIndex(buffer: TimeMotionBuffer, index: number): AllPlanetsSnapshot {
    const clampedIndex = Math.max(0, Math.min(index, buffer.data.length - 1));
    const point = buffer.data[clampedIndex];

    return {
      timestamp: new Date(point.t),
      timestampStr: point.t,
      utcOffset: point.o,
      planets: this.parsePlanetsFromPoint(point),
    };
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Merge new points into existing buffer, maintaining sort order by degree offset
   */
  private mergePoints(existing: DegreeMotionPoint[], newPoints: DegreeMotionPoint[]): DegreeMotionPoint[] {
    const merged = [...existing];
    const existingOffsets = new Set(existing.map(p => p.d));

    for (const point of newPoints) {
      if (!existingOffsets.has(point.d)) {
        merged.push(point);
        existingOffsets.add(point.d);
      }
    }

    // Sort by degree offset
    return merged.sort((a, b) => a.d - b.d);
  }

  /**
   * Sleep utility for polling
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Parse planet string from buffer point
   */
  parsePlanetString(value: string): PlanetPosition {
    const parts = value.split('|');
    if (parts.length !== 4) {
      throw new Error(`Invalid planet string format: ${value}`);
    }

    const [rashiIndex, degreesInRashi, longitude, speed] = parts.map(Number);

    if (isNaN(rashiIndex) || isNaN(degreesInRashi) || isNaN(longitude) || isNaN(speed)) {
      throw new Error(`Invalid numeric values in planet string: ${value}`);
    }

    return { rashiIndex, degreesInRashi, longitude, speed };
  }

  /**
   * Parse all planets from a data point
   */
  private parsePlanetsFromPoint(point: Record<string, any>): Record<string, PlanetPosition> {
    const planets: Record<string, PlanetPosition> = {};
    const planetCodes = ['Lg', 'Sy', 'Ch', 'Ma', 'Bu', 'Gu', 'Sk', 'Sa', 'Ra', 'Ke'] as const;

    for (const code of planetCodes) {
      const value = point[code];
      if (value) {
        try {
          planets[code] = this.parsePlanetString(value);
        } catch (e) {
          // Skip invalid entries
        }
      }
    }
    return planets;
  }

  /**
   * Get all planet positions at a specific index in degree buffer
   */
  getAllPlanetsAtDegreeIndex(buffer: DegreeMotionBuffer, index: number): AllPlanetsSnapshot {
    const clampedIndex = Math.max(0, Math.min(index, buffer.data.length - 1));
    const point = buffer.data[clampedIndex];

    return {
      timestamp: new Date(point.t),
      timestampStr: point.t,
      utcOffset: point.o,
      planets: this.parsePlanetsFromPoint(point),
    };
  }

  // ===========================================================================
  // Universal Buffer Index Methods (DRY)
  // ===========================================================================

  /**
   * Find actual center index in buffer data.
   * During progressive loading, centerIndex may point beyond data length.
   * This finds the real index of d=0 (degree) or i=0 (time) point.
   */
  findActualCenterIndex(buffer: DegreeMotionBuffer | TimeMotionBuffer): number {
    // Determine offset field based on buffer type
    const isDegreeBuffer = 'd' in (buffer.data[0] || {});

    if (isDegreeBuffer) {
      const d0Index = (buffer as DegreeMotionBuffer).data.findIndex(p => p.d === 0);
      return d0Index >= 0 ? d0Index : Math.min(buffer.centerIndex, buffer.data.length - 1);
    } else {
      const i0Index = (buffer as TimeMotionBuffer).data.findIndex(p => p.i === 0);
      return i0Index >= 0 ? i0Index : Math.min(buffer.centerIndex, buffer.data.length - 1);
    }
  }

  /**
   * Safely get point at index with bounds clamping.
   * Returns null if buffer is empty.
   */
  getPointAtIndex<T extends DegreeMotionBuffer | TimeMotionBuffer>(
    buffer: T,
    index: number
  ): T['data'][number] | null {
    if (buffer.data.length === 0) return null;
    const clampedIndex = Math.max(0, Math.min(index, buffer.data.length - 1));
    return buffer.data[clampedIndex];
  }

  /**
   * Get center point (d=0 or i=0) from buffer.
   * Handles progressive loading case where centerIndex may be invalid.
   */
  getCenterPoint<T extends DegreeMotionBuffer | TimeMotionBuffer>(
    buffer: T
  ): T['data'][number] | null {
    const actualIndex = this.findActualCenterIndex(buffer);
    return this.getPointAtIndex(buffer, actualIndex);
  }

  /**
   * Get all planet positions at actual center (d=0 or i=0).
   * Safe version that handles progressive loading.
   */
  getAllPlanetsAtCenter(buffer: DegreeMotionBuffer | TimeMotionBuffer): AllPlanetsSnapshot | null {
    const centerPoint = this.getCenterPoint(buffer);
    if (!centerPoint) return null;

    return {
      timestamp: new Date(centerPoint.t),
      timestampStr: centerPoint.t,
      utcOffset: centerPoint.o,
      planets: this.parsePlanetsFromPoint(centerPoint),
    };
  }

  /**
   * Clear all cached buffers
   */
  clearCache(): void {
    this.degreeCache.clear();
    this.timeCache.clear();
  }

  // ===========================================================================
  // Degree Cache Methods
  // ===========================================================================

  private createDegreeCacheKey(planet: string, startTime: Date, lat: number, lon: number, degreeRange: number, degreeStep: number = 1): string {
    return `degree_${planet}_${formatCacheKeyBase(startTime, lat, lon)}_${degreeRange}_step${degreeStep}`;
  }

  private getFromDegreeCache(key: string): DegreeMotionBuffer | null {
    const entry = this.degreeCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      this.degreeCache.delete(key);
      return null;
    }

    return entry.buffer;
  }

  private addToDegreeCache(key: string, buffer: DegreeMotionBuffer): void {
    if (this.degreeCache.size >= MAX_CACHE_SIZE) {
      this.pruneDegreeCache();
    }

    this.degreeCache.set(key, {
      buffer,
      timestamp: Date.now(),
    });
  }

  private pruneDegreeCache(): void {
    const now = Date.now();
    const entries = Array.from(this.degreeCache.entries());

    for (const [key, entry] of entries) {
      if (now - entry.timestamp > CACHE_TTL_MS) {
        this.degreeCache.delete(key);
      }
    }

    if (this.degreeCache.size >= MAX_CACHE_SIZE) {
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = sorted.slice(0, Math.ceil(MAX_CACHE_SIZE / 2));
      for (const [key] of toRemove) {
        this.degreeCache.delete(key);
      }
    }
  }

  // ===========================================================================
  // Time Cache Methods
  // ===========================================================================

  private createTimeCacheKey(startTime: Date, lat: number, lon: number, stepUnit: TimeStepUnit, stepValue: number, range: number): string {
    return `time_${stepUnit}_${stepValue}_${formatCacheKeyBase(startTime, lat, lon)}_${range}`;
  }

  private getFromTimeCache(key: string): TimeMotionBuffer | null {
    const entry = this.timeCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      this.timeCache.delete(key);
      return null;
    }

    return entry.buffer;
  }

  private addToTimeCache(key: string, buffer: TimeMotionBuffer): void {
    if (this.timeCache.size >= MAX_CACHE_SIZE) {
      this.pruneTimeCache();
    }

    this.timeCache.set(key, {
      buffer,
      timestamp: Date.now(),
    });
  }

  private pruneTimeCache(): void {
    const now = Date.now();
    const entries = Array.from(this.timeCache.entries());

    for (const [key, entry] of entries) {
      if (now - entry.timestamp > CACHE_TTL_MS) {
        this.timeCache.delete(key);
      }
    }

    if (this.timeCache.size >= MAX_CACHE_SIZE) {
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = sorted.slice(0, Math.ceil(MAX_CACHE_SIZE / 2));
      for (const [key] of toRemove) {
        this.timeCache.delete(key);
      }
    }
  }
}

// =============================================================================
// Singleton Export
// =============================================================================

export const motionBufferService = new MotionBufferService();

// =============================================================================
// Utility Functions (exported for testing)
// =============================================================================

/**
 * Parse planet string (exported for testing)
 */
export function parsePlanetString(value: string): PlanetPosition {
  return motionBufferService.parsePlanetString(value);
}

/**
 * Interpolate longitude with 360° wrap-around
 */
export function interpolateLongitude(lon1: number, lon2: number, t: number): number {
  let l1 = lon1;
  let l2 = lon2;

  if (Math.abs(l2 - l1) > 180) {
    if (l2 > l1) l1 += 360;
    else l2 += 360;
  }

  const result = l1 + (l2 - l1) * t;
  return ((result % 360) + 360) % 360;
}

/**
 * Interpolate timestamp
 */
export function interpolateTimestamp(t1: Date, t2: Date, t: number): Date {
  const ms1 = t1.getTime();
  const ms2 = t2.getTime();
  return new Date(ms1 + (ms2 - ms1) * t);
}
